/**
 * The setup page's map card (first on the page) and, for boards with their
 * own rules, the special-rules card right under it.
 */
import React, { useState } from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import type { MapDef } from '@warcarp/api-types'
import { MapThumbnail } from '../../map/MapThumbnail'
import { describeFixedSetup } from '../../../lib/fixedSetup'
import { describeSetupValue, hasSpecialRules, mapFacts } from '../../../lib/gameSetupDraft'
import { SetupCard } from './SetupCards'
import { SETUP_AMBER } from './SetupControls'

export function SetupMapCard({ mapId, mapName, map, wide, edit, thumbWidth, onChangeMap }: {
  mapId: string
  mapName: string
  map: MapDef | null | undefined
  /** Desktop/tablet: thumbnail beside the details. Phone: a banner above them. */
  wide: boolean
  edit: boolean
  /** Phone banner width (the card's inner width). */
  thumbWidth: number
  onChangeMap: () => void
}) {
  const star = hasSpecialRules(map) ? ' ★' : ''
  const facts = map ? mapFacts(map) : ''
  const change = (label: string) => <TouchableOpacity onPress={onChangeMap} accessibilityRole="button" accessibilityLabel="Change map"
    testID="map-picker-trigger" className="rounded-md border-2 border-olive bg-paper-up" style={{ paddingHorizontal: 10, paddingVertical: 5, alignSelf: wide ? 'flex-start' : 'center' }}>
    <Text className="font-typed text-ink text-sm">{label}</Text>
  </TouchableOpacity>
  if (wide) {
    return <View testID="setup-map-card" className="bg-paper border-2 border-olive rounded-xl flex-row items-center"
      style={{ padding: 12, paddingHorizontal: 14, gap: 16 }}>
      <MapThumbnail mapId={mapId} width={210} height={104} />
      <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
        <Text className="font-stencil text-[10px] uppercase tracking-widest text-olive-up">Map</Text>
        <Text className="font-stencil text-ink" style={{ fontSize: 18 }} numberOfLines={1}>{mapName}{star}</Text>
        <Text className="font-typed text-ink-soft text-xs">{facts}{edit && facts ? ' · Seats are kept if you change the map' : ''}</Text>
        <View style={{ marginTop: 6 }}>{change('Change map ›')}</View>
      </View>
    </View>
  }
  return <View testID="setup-map-card" className="bg-paper border-2 border-olive rounded-xl" style={{ overflow: 'hidden' }}>
    <MapThumbnail mapId={mapId} width={thumbWidth} height={120} />
    <View className="flex-row items-center" style={{ gap: 10, paddingHorizontal: 14, paddingVertical: 12 }}>
      <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
        <Text className="font-stencil text-ink" style={{ fontSize: 18 }} numberOfLines={1}>{mapName}{star}</Text>
        <Text className="font-typed text-ink-soft text-xs">{facts}</Text>
        {edit && <Text className="font-typed text-ink-soft text-xs">Seats are kept if you change the map</Text>}
      </View>
      {change('Change ›')}
    </View>
  </View>
}

/** What the board's special rules fix, as short chips ("Players: 2–6"). */
export function specialRuleChips(map: MapDef): string[] {
  const fixed = describeFixedSetup(map)
  if (!fixed) return []
  return [
    `Players: ${fixed.players}`,
    `Territory selection: ${fixed.homeArea.toLowerCase()}`,
    'Starting troops: set by map',
    ...Object.entries(map.fixedSetup?.config ?? {}).map(([key, value]) => {
      const shown = describeSetupValue(key, value) ?? fixed.pinnedConfig.find((p) => p.key === key)
      return shown ? `${shown.label}: ${shown.value}` : key
    }),
  ]
}

/**
 * The board's special rules, collapsed by default: the first rule (clipped)
 * and the chips of what the map fixes; "Show all rules" expands the list.
 */
export function SpecialRulesCard({ map, padding }: { map: MapDef; padding: number }) {
  const chips = specialRuleChips(map)
  const rules = map.specialRules ?? []
  const [open, setOpen] = useState(false)
  const shown = open ? rules : rules.slice(0, 1)
  return <SetupCard padding={padding} accent={SETUP_AMBER} testID="setup-special-rules">
    <View className="flex-row items-center" style={{ marginBottom: 8, gap: 10 }}>
      <Text accessibilityRole="header" className="font-stencil text-ink uppercase flex-1" style={{ fontSize: 15 }}>★ Special rules</Text>
      {rules.length > 1 && <TouchableOpacity testID="setup-special-rules-toggle" accessibilityRole="button"
        accessibilityLabel={open ? 'Show fewer special rules' : `Show all ${rules.length} special rules`}
        accessibilityState={{ expanded: open }} onPress={() => setOpen((v) => !v)} hitSlop={8}>
        <Text className="font-stencil text-ink text-xs">{open ? 'Show less ▴' : `Show all rules (${rules.length}) ▾`}</Text>
      </TouchableOpacity>}
    </View>
    <View style={{ gap: 4 }}>
      {shown.map((rule) => <View key={rule} className="flex-row items-start">
        <Text className="font-typed text-ink-soft text-sm mr-2">•</Text>
        <Text className="font-typed text-ink text-sm flex-1 leading-5" numberOfLines={open ? undefined : 2}>{rule}</Text>
      </View>)}
    </View>
    {chips.length > 0 && <View className="flex-row flex-wrap" style={{ gap: 8, marginTop: 10 }}>
      {chips.map((chip) => <View key={chip} className="rounded-full border border-olive bg-paper-up" style={{ paddingHorizontal: 10, paddingVertical: 4 }}>
        <Text className="font-typed text-ink text-xs">{chip}</Text>
      </View>)}
    </View>}
  </SetupCard>
}
